import { UseDoubleBarChartOptionsProps } from '@components/design/migrated/DoubleBarChart/hooks/useDoubleBarChart';
import { DoubleBarChartSeries } from '@components/design/migrated/DoubleBarChart/Factories/buildDoubleBarChartOptions';
import { ColumnConfig } from '@components/design/migrated/Table';
import classNames from 'classnames';
import ReactDOMServer from 'react-dom/server';
import { ColumnHeaderForPA, PerformanceAnalysisboardPg } from '@models/PerformanceAnalysisboard';
import { formatKpi, getKpiByGrowthTypes, getKpiValue } from '@components/performance-analysis-board/utils/KpiUtils';
import { colors } from '@ecosystem/newron-design-system';

import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { displayOption } from '../utilities/CommonOptions';
import { displayChangeType } from '../utilities/ChartUtils';
import { constructCurrencyLabel, formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { FactRevenue } from '@utils/pageConfig/Types';

interface FormatterProps {
  headerKey: string;
  currencySymbol: string;
  performanceAnalysisboardPg: PerformanceAnalysisboardPg[];
  kpisConfig: ColumnConfig[];
  growthType: GrowthTypes;
  factRevenue: FactRevenue;
}

export const getChartOptions = ({
  headerKey,
  currencySymbol,
  performanceAnalysisboardPg,
  kpisConfig,
  growthType,
  factRevenue,
}: FormatterProps): UseDoubleBarChartOptionsProps => {
  const { standardColors } = colors.useChartColors();
  // first get the default kpiLabel
  const kpiConfig = kpisConfig.find((config) => config.headerKey === headerKey);
  let kpiLabel = kpiConfig?.name.toString();

  // format the currency label into it if needed
  if (kpiLabel) {
    kpiLabel = formatLabelIntoRevenuePriceTitle(kpiLabel, constructCurrencyLabel(factRevenue));
  }

  const getBarChartColor = (idx: number): string | undefined => {
    if (idx === 0) {
      return standardColors[0];
    }
    if (idx >= 3) {
      return standardColors[12];
    }
    return undefined;
  };

  const series: DoubleBarChartSeries[] = [
    {
      name: kpiLabel ?? 'Series 1',
      data: performanceAnalysisboardPg.map((pg, idx) => {
        const leftValue = getKpiValue(pg, headerKey);
        const rightValue = getKpiByGrowthTypes(pg, headerKey, growthType);
        const color = getBarChartColor(idx);
        return {
          leftValue,
          rightValue,
          color,
        };
      }),
    },
  ];

  const rightFormatter = (value: number): string => {
    return formatKpi(headerKey as ColumnHeaderForPA, value, {
      currencySymbol,
      as: displayOption(headerKey, growthType),
    });
  };
  const leftFormatter = (value: number): string => {
    return formatKpi(headerKey as ColumnHeaderForPA, value, { currencySymbol });
  };

  const rowData = performanceAnalysisboardPg.map((pg) => {
    return { ...pg, label: pg.segmentOrPg, rowHeight: 40 };
  });

  const tooltipFormatter = (row: PerformanceAnalysisboardPg): string => {
    const changeValue = getKpiByGrowthTypes(row, headerKey, growthType);

    const formattedChangeValue = formatKpi(headerKey as ColumnHeaderForPA, changeValue, {
      currencySymbol,
      as: displayOption(headerKey, growthType),
    });
    return ReactDOMServer.renderToString(
      <div className="bg-white border-0 text-sm p-4 w-full" data-test-id={'chart-tooltip'}>
        <div className="flex border-b border-ext-grey-400 text-lighter font-normal mb-2 pb-2">
          <div>
            <div className="font-bold">{row.segmentOrPg}</div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-start w-full pb-1">
            <p className="text-lighter overflow-ellipses" style={{ maxWidth: '280px' }}>
              {kpiLabel}
            </p>
            <p className="self-end text-base font-bold justify-end ml-auto pl-2">
              {formatKpi(headerKey as ColumnHeaderForPA, getKpiValue(row, headerKey), {
                currencySymbol,
              })}
            </p>
          </div>
          <div className="flex items-center justify-start w-full pb-1">
            <p className="text-lighter overflow-ellipses" style={{ maxWidth: '280px' }}>
              {kpiLabel} Change {displayChangeType(growthType, headerKey)}
            </p>
            <p
              className={classNames(
                'self-end text-base font-bold justify-end ml-auto pl-2',
                {
                  'text-ui-green': changeValue > 0,
                },
                {
                  'text-ui-red': changeValue < 0,
                },
              )}
            >
              {formattedChangeValue}
            </p>
          </div>
        </div>
      </div>,
    );
  };
  return {
    leftTitle: performanceAnalysisboardPg.length !== 0 ? 'Absolute' : '', // onClick no data still shows title
    rightTitle: 'Change',
    leftFormatter,
    rightFormatter,
    tooltipFormatter,
    rows: rowData,
    series,
  };
};
